import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { buttonLabels } from "../shared/constants";
import ButtonComponent from "../shared/button/button.component";
import { Autocomplete, FormHelperText, Grid, TextField } from "@mui/material";
import * as selector from "../../selectors/selectors"
import ArrowTimePickerComponent from "../shared/datetimepicker/arrow-timepicker.component";
import setToast from "../../helpers/toaster.helper";
import { ToasterType } from "../shared/toaster/toaster.model";
import { BusinessHoursModel, DaysInAWeek } from "../../models/settings-model";
import { UserService } from "../../services/UserService";
import SelectBusinessHoursComponent from "./select-business-hours.component";
import { SettingsService } from "../../services/SettingsService";
import moment from "moment";
import CustomOutOfMessage from "./custom-out-of-office-message";
import { validateForm } from "../shared/form-validation";
import { GetBusinessHoursStatus } from "../../actions";
import { UtilityService } from "../../services/UtilityService";
import { MediaModel } from "../../models/conversations";


interface Props {
    IsActive: boolean
    handleSwitch: (e) => any
}

const BusinessHoursComponent: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch()
    const time = (hours: number, minutes: number = 0) => {
        let currentDate = new Date();
        currentDate.setHours(hours, minutes, 0, 0);
        return currentDate.getTime();
    }

    const InitialBusinessHours: BusinessHoursModel[] = [
        {
            DAY: DaysInAWeek.MONDAY,
            OPEN_TIME: time(9),
            CLOSE_TIME: time(1),
            SELECTED: false,
        }, {
            DAY: DaysInAWeek.TUESDAY,
            OPEN_TIME: time(9),
            CLOSE_TIME: time(18),
            SELECTED: false,
        }, {
            DAY: DaysInAWeek.WEDNESDAY,
            OPEN_TIME: time(9),
            CLOSE_TIME: time(18),
            SELECTED: false,
        }, {
            DAY: DaysInAWeek.THURSDAY,
            OPEN_TIME: time(9),
            CLOSE_TIME: time(18),
            SELECTED: false,
        }, {
            DAY: DaysInAWeek.FRIDAY,
            OPEN_TIME: time(9),
            CLOSE_TIME: time(18),
            SELECTED: false,
        }, {
            DAY: DaysInAWeek.SATURDAY,
            OPEN_TIME: time(9),
            CLOSE_TIME: time(18),
            SELECTED: false,
        }, {
            DAY: DaysInAWeek.SUNDAY,
            OPEN_TIME: time(9),
            CLOSE_TIME: time(18),
            SELECTED: false,
        }
    ]

    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);

    const [BusinessHours, setBusinessHours] = useState<BusinessHoursModel[]>(InitialBusinessHours);
    const [alwaysOpen, setAlwaysOpen] = useState<boolean>(false);
    const [PermananentlyClosed, setPermanentlyClosed] = useState<boolean>(false);
    const [isloading, setIsLoading] = useState<boolean>(false);
    const [businessHoursType, setBusinessHoursType] = useState<string>("Custom")
    const [customBusinessHours, setCustomBusinessHours] = useState(InitialBusinessHours);
    const [commonStartTime, setCommonStartTime] = useState<number>(time(9))
    const [commonEndTime, setCommonEndTime] = useState<number>(time(18))
    const [userinput, setUserinput] = useState({
        ID: "",
        Title: "",
        Message: "",
        UpdatedDateTime: "",
        CreatedByID: "",
    })
    const [errors, setErrors] = useState<{
        Title: string;
        Message: string;
        Type: string;
        [key: string]: any;
    }>({
        Title: "",
        Message: "",
        Type: ""
    })


    const handleBusinessHours = (data) => {
        const newBusinessHours = BusinessHours.map(x => x.DAY === data.DAY ? data : x)
        if (newBusinessHours.find(x => x.SELECTED === true)) {
            setPermanentlyClosed(false)
            setAlwaysOpen(false)
        }
        setBusinessHours(newBusinessHours)
    }

    const createBusinessHours = () => {
        let obj = {
            "phoneNumberId": UserService.getUserPhoneNumberId(),
            "timeZone": SettingsService.getUserPreferredTimeZone(),
            "WorkHours": BusinessHours.map(x => ({
                dayOfTheWeek: x.DAY, fromHour: x.SELECTED ? moment(x.OPEN_TIME).format("HH:mm") : null, toHour: x.SELECTED ? moment(x.CLOSE_TIME).format("HH:mm") : null
            })),
            "isDeleted": false,
            "isActive": props.IsActive,
            "message": userinput.Message,
            "type": businessHoursType,
        }
        if (validateForm({ afterhours: userinput.Message }, (validationErrors) => {
            setErrors({ ...errors, Message: validationErrors.afterhours })
        })) {
            setIsLoading(true)
            SettingsService.CreateBusinessHours(obj).then((res) => {
                setIsLoading(false)
                setToast("Business hours have been updated successfully", ToasterType.SUCCESS)
                GetBusinessHoursStatus(dispatch)
            }).catch((err) => {
                setIsLoading(false)
            })
        }
    }


    useLayoutEffect(() => {
        SettingsService.GetBusinessHours().then((res) => {
            if (!res.data || !res.data.workHours) {
                return;
            }

            handleBusinessHoursType(res.data.type)

            setUserinput((prevData) => ({
                ...prevData, Message: res.data.message
            }))

            props.handleSwitch(res.data.isActive)

            const updatedBusinessHours = BusinessHours.map(dayConfig => {

                const apiDay = res.data.workHours.find(
                    workHour => workHour.dayOfTheWeek.toLowerCase() === dayConfig.DAY.toLowerCase()
                );

                if (!apiDay || !apiDay.fromHour || !apiDay.toHour) {
                    return { ...dayConfig, SELECTED: false };
                }

                try {
                    const [fromHours, fromMinutes] = apiDay.fromHour.split(":").map(Number);
                    const [toHours, toMinutes] = apiDay.toHour.split(":").map(Number);

                    if (isNaN(fromHours) || isNaN(fromMinutes) || isNaN(toHours) || isNaN(toMinutes)) {
                        throw new Error('Invalid time format');
                    }

                    return {
                        ...dayConfig,
                        OPEN_TIME: time(fromHours, fromMinutes),
                        CLOSE_TIME: time(toHours, toMinutes),
                        SELECTED: true
                    };
                } catch (error) {
                    console.error(`Error parsing time for ${dayConfig.DAY}:`, error);
                    return dayConfig;
                }
            });
            const firstValidDay = updatedBusinessHours.find(x => x.SELECTED && x.OPEN_TIME && x.CLOSE_TIME);

            setBusinessHours(updatedBusinessHours);
            setCommonStartTime(firstValidDay ? firstValidDay.OPEN_TIME : time(9));
            setCommonEndTime(firstValidDay ? firstValidDay.CLOSE_TIME : time(18));
        }).catch((err) => {
            console.error('Error fetching business hours:', err);
            setBusinessHours(InitialBusinessHours);
        });
    }, []);

    useEffect(() => {
        if (!props.IsActive) {
            setErrors({...errors , Message : ""});
        }
    }, [props.IsActive]);



    const handleBusinessHoursType = (value) => {
        setBusinessHoursType(value)
        if (businessHoursType === "Custom" && value !== "Custom") {
            setCustomBusinessHours([...BusinessHours]);
        }
        const defaultStart = time(9);
        const defaultEnd = time(18);
        if (value === "Daily") {
            setCommonStartTime(defaultStart);
            setCommonEndTime(defaultEnd)
            setBusinessHours(InitialBusinessHours.map(x => { return { ...x, OPEN_TIME: defaultStart, CLOSE_TIME: defaultEnd, SELECTED: true } }))
            setAlwaysOpen(false)
            setPermanentlyClosed(false)
        }
        if (value === "Weekdays") {
            setCommonStartTime(defaultStart);
            setCommonEndTime(defaultEnd)
            setBusinessHours(InitialBusinessHours.map(x => { return (x.DAY != ("Sunday") && x.DAY != ("Saturday")) ? { ...x, OPEN_TIME: defaultStart, CLOSE_TIME: defaultEnd, SELECTED: true } : { ...x, SELECTED: false } }))
            setAlwaysOpen(false)
            setPermanentlyClosed(false)
        }
        if (value === "Weekend") {
            setCommonStartTime(defaultStart);
            setCommonEndTime(defaultEnd)
            setBusinessHours(InitialBusinessHours.map(x => { return (x.DAY === ("Sunday") || x.DAY === ("Saturday")) ? { ...x, OPEN_TIME: defaultStart, CLOSE_TIME: defaultEnd, SELECTED: true } : { ...x, SELECTED: false } }))
            setAlwaysOpen(false)
            setPermanentlyClosed(false)
        }
        if (value === "Custom") {
            setBusinessHours(customBusinessHours)
            setCommonStartTime(time(9))
            setCommonEndTime(time(18))
        }
    }

    const handleOpenTime = (time) => {
        setCommonStartTime(time)
        const businessHours = BusinessHours.map(x => {
            return { ...x, OPEN_TIME: time }
        })
        setBusinessHours(businessHours)
    }

    const handleCloseTime = (time) => {
        setCommonEndTime(time)
        const businessHours = BusinessHours.map(x => {
            return { ...x, CLOSE_TIME: time }
        })
        setBusinessHours(businessHours)
    }
    const handleMessageChanges = (data: { body: string, media: MediaModel[], bodyLength: number, characterLimit: number, }) => {
        if (!props.IsActive) return;
        if (validateForm({ afterhours: data.body }, (validationErrors) => {
            setErrors({ ...errors, Message: validationErrors.afterhours })
        })) {
            if ((!data.body) || (data.body.length < 1)) {
                setUserinput({ ...userinput, Message: data.body })
                setErrors({ ...errors, Message: "Message cannot be blank" })
            } else {
                setUserinput({ ...userinput, Message: data.body })
                setErrors({ ...errors, Message: "" })
            }
            if (data?.body?.length > data?.characterLimit) { setErrors({ ...errors, Message: `Message should not exceed ${data?.characterLimit} characters` }) }
        }
    }

    return (
        <>
            <div className={`${IsMobileDevice ? "" : ""} w-p-100 ${!props.IsActive ? 'disabled bg-color-settings-background' : null}`}>
                <div className={`w-p-100 d-flex align-center ${IsMobileDevice ? "my-2 justify-between" : ""}`}>
                    <Autocomplete
                        disableClearable
                        classes={{ input: 'h5-medium color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
                        value={UtilityService.trimMessage(businessHoursType, 6)}
                        size="small"
                        className={` ${IsMobileDevice ? "w-p-30 mr-1" : "w-18 mr-4"} h-p-100 `}
                        options={["Custom", "Daily", "Weekdays", "Weekend"]}
                        onChange={(e, value) => { handleBusinessHoursType(value) }}
                        renderInput={
                            (params) =>
                                <TextField
                                    {...params}
                                    name="BusinessHours Type"
                                    variant="outlined"
                                    label="Type"
                                    inputProps={{ ...params.inputProps, readOnly: true }}
                                />
                        }
                    />
                    {businessHoursType !== 'Custom' &&
                        <>
                            <div className={` ${IsMobileDevice ? "w-p-33" : "w-15"} h-p-100`}>
                                <ArrowTimePickerComponent Value={commonStartTime} handleChange={handleOpenTime} TextLabel={""} IsMobileDevice={IsMobileDevice} ClassName={"br-1"} IsDisabled={false} />
                            </div>
                            <div className="w-p-4 h-p-100 d-flex justify-center align-center">
                                -
                            </div>
                            <div className={` ${IsMobileDevice ? "w-p-33" : "w-15"} h-p-100`}>
                                <ArrowTimePickerComponent Value={commonEndTime} handleChange={handleCloseTime} TextLabel={""} IsMobileDevice={IsMobileDevice} ClassName={"br-1"} IsDisabled={false} />
                            </div>
                        </>
                    }
                </div>
                {
                    businessHoursType === "Custom" &&
                    <div className={`${IsMobileDevice ? "h-p-100" : "h-p-60"}`}>
                        <Grid container className="h-p-100 w-p-100">
                            {
                                BusinessHours.map((x: BusinessHoursModel) => (
                                    <Grid item md={6} className="h-p-100 w-p-100">
                                        <SelectBusinessHoursComponent BusinessHours={x} IsMobileDevice={IsMobileDevice} handleBusinessHours={handleBusinessHours} DisableSelect={alwaysOpen || PermananentlyClosed} />
                                    </Grid>
                                ))
                            }
                        </Grid>
                    </div>
                }
                {businessHoursType != "Custom" &&
                    <div className="d-flex mt-1">
                        {
                            BusinessHours.map(x => {
                                return (
                                    <div className={`w-4 h-4 br-p-50 ${x.SELECTED ? "bg-color-primary-light" : "bg-color-settings-nav-bar-option"} color-secondary d-flex align-center justify-center h5-medium mr-1`}>
                                        {x.DAY.slice(0, 1)}
                                    </div>
                                )
                            })
                        }
                    </div>
                }
                <div className={`mt-1 ${IsMobileDevice ? " w-p-100 px-1" : "mr-1 w-p-100 d-flex justify-between"}`}>
                    <div className="py-1 h5-semibold">Custom Out-of-Office Message</div>
                    <FormHelperText className={`h6-regular color-primary-danger t-align-right${errors.Message ? "" : "py-1"}`}>
                        {errors.Message}
                    </FormHelperText>
                </div>
                <CustomOutOfMessage
                    userinput={userinput}
                    handleMessageChanges={handleMessageChanges}
                    AfterHoursClick={createBusinessHours}
                    IsAfterHoursActive={!props.IsActive} />
            </div>
            <div className="">
                <ButtonComponent
                    OnClick={() => { createBusinessHours() }}
                    ButtonLabel={buttonLabels.SAVE}
                    Loading={isloading}
                    Variant={((BusinessHours?.find(x => x.SELECTED === true) || alwaysOpen || PermananentlyClosed) && !errors.Message) ? "primary-btn-small" : "disabled-btn-small"}
                    ClassName={`c-pointer w-space ml-auto mr-2`}
                />
            </div>
        </>
    )
}

export default BusinessHoursComponent;